import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";
const LOCAL_TURNSTILE_TOKEN = "dummy-token-for-development";

const loadTurnstileScript = () => {
  if (window.turnstile) {
    return Promise.resolve();
  }

  const existingScript = document.querySelector('script[data-turnstile-script="true"]');
  if (existingScript) {
    return new Promise((resolve, reject) => {
      existingScript.addEventListener("load", resolve, { once: true });
      existingScript.addEventListener("error", reject, { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.dataset.turnstileScript = "true";
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const AdminLoginPage = () => {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileConfig, setTurnstileConfig] = useState({ enabled: false, site_key: "" });
  const [turnstileLoading, setTurnstileLoading] = useState(true);
  const widgetRef = useRef(null);
  const widgetIdRef = useRef(null);

  const { isAuthenticated, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/admin/dashboard";

  useEffect(() => {
    let mounted = true;

    const loadConfig = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/auth/turnstile-config`);
        if (!response.ok) throw new Error("Config Turnstile gagal dimuat");
        const data = await response.json();
        if (!mounted) return;
        setTurnstileConfig(data);
        if (!data.enabled) {
          setTurnstileToken(LOCAL_TURNSTILE_TOKEN);
        }
      } catch (error) {
        if (!mounted) return;
        setTurnstileConfig({ enabled: false, site_key: "" });
        setTurnstileToken(LOCAL_TURNSTILE_TOKEN);
      } finally {
        if (mounted) setTurnstileLoading(false);
      }
    };

    loadConfig();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const renderTurnstile = async () => {
      if (!turnstileConfig.enabled || !turnstileConfig.site_key || !widgetRef.current) return;

      try {
        await loadTurnstileScript();
        if (!mounted || !window.turnstile || !widgetRef.current) return;

        widgetRef.current.innerHTML = "";
        widgetIdRef.current = window.turnstile.render(widgetRef.current, {
          sitekey: turnstileConfig.site_key,
          callback: (token) => setTurnstileToken(token),
          "expired-callback": () => {
            setTurnstileToken("");
            toast.error("Verifikasi keamanan kedaluwarsa, silakan ulangi");
          },
          "error-callback": (error) => {
            setTurnstileToken("");
            console.error("Turnstile error:", error);
            toast.error("Verifikasi keamanan gagal dimuat");
          },
        });
      } catch (error) {
        console.error("Turnstile script error:", error);
        toast.error("Script verifikasi keamanan gagal dimuat");
      }
    };

    renderTurnstile();
    return () => {
      mounted = false;
      if (widgetIdRef.current && window.turnstile?.remove) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [turnstileConfig]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, from, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!turnstileToken) {
      toast.error("Silakan selesaikan verifikasi keamanan");
      return;
    }

    setIsLoading(true);
    try {
      const result = await login({
        identifier,
        password,
        turnstile_token: turnstileToken,
      });

      if (result.success) {
        toast.success("Login berhasil");
        navigate(from, { replace: true });
      } else {
        toast.error(result.error || "Login gagal");
      }
    } catch (error) {
      toast.error(error.message || "Terjadi kesalahan saat login");
    } finally {
      setIsLoading(false);
      if (turnstileConfig.enabled && window.turnstile?.reset && widgetIdRef.current) {
        window.turnstile.reset(widgetIdRef.current);
        setTurnstileToken("");
      }
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-gray-900" />
          <p className="mt-4 text-gray-600">Memeriksa status autentikasi...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Login Admin</CardTitle>
          <CardDescription>Masuk ke dashboard administrasi</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="identifier">Email / Username / No. HP</Label>
              <Input
                id="identifier"
                type="text"
                placeholder="Email, username, atau no. HP"
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                disabled={isLoading}
              />
            </div>

            {turnstileConfig.enabled ? (
              <div className="space-y-2">
                <Label>Verifikasi Keamanan</Label>
                <div ref={widgetRef} className="min-h-[74px]" />
              </div>
            ) : (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                Verifikasi keamanan nonaktif untuk environment lokal.
              </div>
            )}
          </CardContent>
          <CardFooter className="flex flex-col">
            <Button type="submit" className="w-full" disabled={isLoading || turnstileLoading || !turnstileToken}>
              {isLoading ? "Memproses..." : "Login"}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default AdminLoginPage;
